/**
 * Post Purchases Hook using TanStack Query
 * Handles post-purchase offers with automatic caching
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    CheckoutSessionState,
    CurrencyOptions,
    OrderSummary,
    PostPurchaseOffer,
    PostPurchaseOfferItem,
    PostPurchaseOfferSummary,
    PostPurchasesResource
} from '../../core/resources/postPurchases';
import { useTagadaContext } from '../providers/TagadaProvider';

import { getGlobalApiClient, getGlobalApiClientOrNull } from './useApiQuery';

export interface UsePostPurchasesQueryOptions {
    orderId: string;
    enabled?: boolean;
    autoInitializeCheckout?: boolean;
}

export interface UsePostPurchasesQueryResult {
    // Query data
    offers: PostPurchaseOffer[] | undefined;
    isLoading: boolean;
    error: Error | null;

    // Actions
    acceptOffer: (offerId: string, items: PostPurchaseOfferItem[]) => Promise<{ success: boolean; summary?: PostPurchaseOfferSummary; error?: string }>;
    declineOffer: (offerId: string) => Promise<{ success: boolean; error?: string }>;
    previewOffer: (offerId: string, items: PostPurchaseOfferItem[]) => Promise<{ success: boolean; summary?: PostPurchaseOfferSummary; error?: string }>;
    refresh: () => void;

    // V1 compatibility methods
    payWithCheckoutSession: (checkoutSessionId: string, orderId?: string) => Promise<void>;

    // Missing V1 methods that need to be implemented
    initCheckoutSession: (offerId: string, orderId: string) => Promise<{ checkoutSessionId: string }>;
    initCheckoutSessionWithVariants: (offerId: string, orderId: string, lineItems: { variantId: string; quantity: number }[]) => Promise<{ checkoutSessionId: string }>;
    getOffer: (offerId: string) => PostPurchaseOffer | undefined;
    getTotalValue: () => number;
    getTotalSavings: () => number;

    // Checkout session management
    getCheckoutSessionState: (offerId: string) => CheckoutSessionState | null;
    initializeOfferCheckout: (offerId: string) => Promise<void>;
    getAvailableVariants: (offerId: string, productId: string) => {
        variantId: string;
        variantName: string;
        variantSku: string | null;
        variantDefault: boolean | null;
        variantExternalId: string | null;
        priceId: string;
        currencyOptions: CurrencyOptions;
    }[];
    selectVariant: (offerId: string, productId: string, variantId: string) => Promise<void>;
    getOrderSummary: (offerId: string) => OrderSummary | null;
    isLoadingVariants: (offerId: string, productId: string) => boolean;
    isUpdatingOrderSummary: (offerId: string) => boolean;
    confirmPurchase: (offerId: string, options?: { draft?: boolean; returnUrl?: string }) => Promise<void>;
}

export function usePostPurchasesQuery(options: UsePostPurchasesQueryOptions): UsePostPurchasesQueryResult {
    const { orderId, enabled = true, autoInitializeCheckout = false } = options;
    const queryClient = useQueryClient();
    const { session, isSessionInitialized, apiService } = useTagadaContext();

    // Track if token is available on API service
    const [hasToken, setHasToken] = useState(() => apiService.getCurrentToken() !== null);

    // Update token availability when session is initialized
    useEffect(() => {
        if (isSessionInitialized) {
            const checkToken = () => {
                const token = apiService.getCurrentToken();
                const tokenAvailable = token !== null;
                setHasToken(tokenAvailable);
                // Also ensure API client has the token
                const apiClient = getGlobalApiClientOrNull();
                if (apiClient && token) {
                    apiClient.updateToken(token);
                }
                return tokenAvailable;
            };

            // Check immediately
            if (checkToken()) {
                return; // Token is available, no need to poll
            }

            // Poll for token if not immediately available (handles async token setting)
            const interval = setInterval(() => {
                if (checkToken()) {
                    clearInterval(interval);
                }
            }, 100); // Check every 100ms

            return () => clearInterval(interval);
        }
    }, [isSessionInitialized, apiService]);

    // State for checkout sessions per offer
    const [checkoutSessions, setCheckoutSessions] = useState<Record<string, CheckoutSessionState>>({});
    // Ref to track checkout sessions for synchronous access
    const checkoutSessionsRef = useRef<Record<string, CheckoutSessionState>>({});
    // Ref to track offers being initialized to prevent duplicate initialization
    const initializingOffers = useRef<Set<string>>(new Set());

    // Keep ref in sync with state
    useEffect(() => {
        checkoutSessionsRef.current = checkoutSessions;
    }, [checkoutSessions]);

    // Create post purchases resource client
    const postPurchasesResource = useMemo(() => {
        try {
            return new PostPurchasesResource(getGlobalApiClient());
        } catch (error) {
            throw new Error('Failed to initialize post purchases resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);

    // Helper function for fetching order summary - exact same as V1
    const fetchOrderSummary = useCallback(async (offerId: string, sessionId: string) => {
        try {
            // Set updating state
            setCheckoutSessions(prev => ({
                ...prev,
                [offerId]: {
                    ...prev[offerId],
                    isUpdatingSummary: true,
                }
            }));

            const summaryResult = await postPurchasesResource.getOrderSummary(sessionId, true);

            if (summaryResult) {
                // Sort items by productId to ensure consistent order
                const sortedItems = [...summaryResult.items].sort((a: any, b: any) =>
                    a.productId.localeCompare(b.productId)
                );

                const orderSummary: OrderSummary = {
                    ...summaryResult,
                    items: sortedItems,
                };

                // Initialize selected variants based on the summary
                const initialVariants: Record<string, string> = {};
                sortedItems.forEach((item: any) => {
                    if (item.productId && item.variantId) {
                        initialVariants[item.productId] = item.variantId;
                    }
                });

                setCheckoutSessions(prev => ({
                    ...prev,
                    [offerId]: {
                        ...prev[offerId],
                        orderSummary,
                        selectedVariants: initialVariants,
                        isUpdatingSummary: false,
                    }
                }));
            }
        } catch (error) {
            setCheckoutSessions(prev => ({
                ...prev,
                [offerId]: {
                    ...prev[offerId],
                    isUpdatingSummary: false,
                }
            }));
            throw error;
        }
    }, [postPurchasesResource]);

    // Initialize checkout session for an offer
    const initializeOfferCheckout = useCallback(async (offerId: string) => {
        try {
            // Check if customer ID is available
            if (!session?.customerId) {
                throw new Error('Customer ID is required. Make sure the session is properly initialized.');
            }

            // Check if already initializing to avoid race conditions
            if (initializingOffers.current.has(offerId)) {
                return; // Already initializing, skip
            }

            // Check if already initialized using ref for synchronous access
            if (checkoutSessionsRef.current[offerId]?.checkoutSessionId) {
                return; // Already initialized, exit early
            }

            // Mark as initializing
            initializingOffers.current.add(offerId);

            try {
                // Initialize checkout session
                const initResult = await postPurchasesResource.initCheckoutSession(offerId, orderId, session.customerId);

                if (!initResult.checkoutSessionId) {
                    throw new Error('Failed to initialize checkout session');
                }

                const sessionId = initResult.checkoutSessionId;

                // Initialize session state
                setCheckoutSessions(prev => {
                    // Double-check we're not overwriting an existing session
                    if (prev[offerId]?.checkoutSessionId) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [offerId]: {
                            checkoutSessionId: sessionId,
                            orderSummary: null,
                            selectedVariants: {},
                            loadingVariants: {},
                            isUpdatingSummary: false,
                        }
                    };
                });

                // Fetch order summary with variant options
                await fetchOrderSummary(offerId, sessionId);

                // Detect Whop payment method for this checkout session
                try {
                    const paymentMethods = await postPurchasesResource.getPaymentMethods(sessionId);
                    const isWhop = paymentMethods.some((m: { type: string }) => m.type === 'whop');
                    if (isWhop) {
                        setCheckoutSessions(prev => ({
                            ...prev,
                            [offerId]: {
                                ...prev[offerId],
                                isWhop: true,
                            }
                        }));
                    }
                } catch (err) {
                    // Non-critical: if payment methods fetch fails, fall back to standard flow
                    console.warn(`[SDK] Failed to detect payment methods for offer ${offerId}:`, err);
                }
            } finally {
                // Remove from initializing set
                initializingOffers.current.delete(offerId);
            }
        } catch (error) {
            // Remove from initializing set on error
            initializingOffers.current.delete(offerId);
            console.error(`[SDK] Failed to initialize checkout for offer ${offerId}:`, error);
            throw error;
        }
    }, [session?.customerId, orderId, postPurchasesResource, fetchOrderSummary]);

    // Main post-purchase offers query
    const {
        data: offers = undefined,
        isLoading,
        error,
        refetch,
    } = useQuery<PostPurchaseOffer[]>({
        queryKey: ['post-purchase-offers', orderId],
        queryFn: async () => {
            // Double-check token is available before making the request
            const token = apiService.getCurrentToken();
            if (!token) {
                throw new Error('No authentication token available. Please wait for session initialization.');
            }
            // Ensure API client has the token
            const apiClient = getGlobalApiClientOrNull();
            if (apiClient && token) {
                apiClient.updateToken(token);
            }
            return postPurchasesResource.getPostPurchaseOffers(orderId);
        },
        enabled: enabled && !!orderId && isSessionInitialized && hasToken, // Wait for CMS token/session to be ready AND token to be set on API client
        staleTime: 30_000, // 30 seconds
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
            // Retry if token is not available yet (might be a timing issue)
            if (error instanceof Error && error.message.includes('No authentication token')) {
                return failureCount < 3; // Retry up to 3 times
            }
            return false; // Don't retry other errors
        },
    });

    // Auto-initialize checkout sessions if enabled
    useEffect(() => {
        if (autoInitializeCheckout && offers && offers.length > 0 && session?.customerId && isSessionInitialized) {
            // Initialize checkout sessions for all offers
            offers.forEach((offer) => {
                // The initializeOfferCheckout function will check if already initialized or initializing
                void initializeOfferCheckout(offer.id).catch((error) => {
                    // Log errors but don't throw - auto-initialization failures shouldn't break the UI
                    console.error(`[SDK] Failed to auto-initialize checkout for offer ${offer.id}:`, error);
                });
            });
        }
    }, [autoInitializeCheckout, offers, session?.customerId, isSessionInitialized, initializeOfferCheckout]);

    // Accept offer mutation
    const acceptMutation = useMutation<
        { success: boolean; summary?: PostPurchaseOfferSummary; error?: string },
        Error,
        { offerId: string; items: PostPurchaseOfferItem[] }
    >({
        mutationFn: ({ offerId, items }) => {
            if (!orderId) {
                throw new Error('No order ID available');
            }

            return postPurchasesResource.acceptOffer(orderId, offerId, items);
        },
        onSuccess: () => {
            // Refresh offers and potentially checkout data
            void refetch();
            if (orderId) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', orderId] });
            }
        },
    });

    // Decline offer mutation
    const declineMutation = useMutation<
        { success: boolean; error?: string },
        Error,
        { offerId: string }
    >({
        mutationFn: ({ offerId }) => {
            if (!orderId) {
                throw new Error('No order ID available');
            }

            return postPurchasesResource.declineOffer(orderId, offerId);
        },
        onSuccess: () => {
            // Refresh offers
            void refetch();
        },
    });

    // Preview offer mutation
    const previewMutation = useMutation<
        { success: boolean; summary?: PostPurchaseOfferSummary; error?: string },
        Error,
        { offerId: string; items: PostPurchaseOfferItem[] }
    >({
        mutationFn: ({ offerId, items }) => {
            if (!orderId) {
                throw new Error('No order ID available');
            }

            return postPurchasesResource.previewOffer(orderId, offerId, items);
        },
        // No onSuccess needed for preview - it doesn't change state
    });

    return {
        // Query data
        offers,
        isLoading,
        error,

        // Actions
        acceptOffer: async (offerId: string, items: PostPurchaseOfferItem[]) => {
            try {
                return await acceptMutation.mutateAsync({ offerId, items });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to accept offer');
                return { success: false, error: error.message };
            }
        },
        declineOffer: async (offerId: string) => {
            try {
                return await declineMutation.mutateAsync({ offerId });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to decline offer');
                return { success: false, error: error.message };
            }
        },
        previewOffer: async (offerId: string, items: PostPurchaseOfferItem[]) => {
            try {
                return await previewMutation.mutateAsync({ offerId, items });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to preview offer');
                return { success: false, error: error.message };
            }
        },
        refresh: () => void refetch(),

        // V1 compatibility methods
        payWithCheckoutSession: async (checkoutSessionId: string, orderId?: string) => {
            await postPurchasesResource.payWithCheckoutSession(checkoutSessionId, orderId);
        },

        // Missing V1 methods implementations
        initCheckoutSession: async (offerId: string, orderId: string) => {
            // Check if customer ID is available
            if (!session?.customerId) {
                throw new Error('Customer ID is required. Make sure the session is properly initialized.');
            }

            return await postPurchasesResource.initCheckoutSession(offerId, orderId, session.customerId);
        },

        initCheckoutSessionWithVariants: async (offerId: string, orderId: string, lineItems: { variantId: string; quantity: number }[]) => {
            return await postPurchasesResource.initCheckoutSessionWithVariants(offerId, orderId, lineItems);
        },

        getOffer: (offerId: string) => {
            return offers?.find(offer => offer.id === offerId);
        },

        getTotalValue: () => {
            return offers?.reduce((total, offer) => {
                return total + offer.summaries.reduce((summaryTotal, summary) => {
                    return summaryTotal + summary.totalAmount;
                }, 0);
            }, 0) ?? 0;
        },

        getTotalSavings: () => {
            return offers?.reduce((total, offer) => {
                return total + offer.summaries.reduce((summaryTotal, summary) => {
                    return summaryTotal + (summary.totalAmount - summary.totalAdjustedAmount);
                }, 0);
            }, 0) ?? 0;
        },

        // Checkout session management - exact same as V1
        getCheckoutSessionState: (offerId: string) => {
            return checkoutSessions[offerId] || null;
        },

        initializeOfferCheckout,

        getAvailableVariants: (offerId: string, productId: string) => {
            const sessionState = checkoutSessions[offerId];
            console.log('sessionState', sessionState);
            if (!sessionState?.orderSummary?.options?.[productId]) return [];
            console.log('getAvailableVariants', offerId, productId);
            console.log('sessionState.orderSummary.options', sessionState.orderSummary.options);
            return sessionState.orderSummary.options[productId].map((variant: any) => ({
                variantId: variant.id,
                variantName: variant.name,
                variantSku: variant.sku,
                variantDefault: variant.default,
                variantExternalId: variant.externalVariantId,
                priceId: variant.prices[0]?.id,
                currencyOptions: variant.prices[0]?.currencyOptions,
            }));
        },

        selectVariant: async (offerId: string, productId: string, variantId: string) => {
            const sessionState = checkoutSessions[offerId];
            if (!sessionState?.checkoutSessionId || !sessionState.orderSummary) {
                throw new Error('Checkout session not initialized for this offer');
            }

            // Set loading state for this specific variant
            setCheckoutSessions(prev => ({
                ...prev,
                [offerId]: {
                    ...prev[offerId],
                    loadingVariants: {
                        ...prev[offerId].loadingVariants,
                        [productId]: true,
                    }
                }
            }));

            try {
                const sessionState = checkoutSessions[offerId];
                if (!sessionState?.orderSummary?.options?.[productId]) {
                    throw new Error('No variants available for this product');
                }

                const availableVariants = sessionState.orderSummary.options[productId].map((variant: any) => ({
                    variantId: variant.id,
                    variantName: variant.name,
                    variantSku: variant.sku,
                    variantDefault: variant.default,
                    variantExternalId: variant.externalVariantId,
                    priceId: variant.prices[0]?.id,
                    currencyOptions: variant.prices[0]?.currencyOptions,
                }));

                const selectedVariant = availableVariants.find(v => v.variantId === variantId);

                if (!selectedVariant) {
                    throw new Error('Selected variant not found');
                }

                // Find the current item to get its quantity
                const currentItem = sessionState.orderSummary.items.find(item => item.productId === productId);
                if (!currentItem) {
                    throw new Error('Current item not found');
                }

                // Update selected variants state
                setCheckoutSessions(prev => ({
                    ...prev,
                    [offerId]: {
                        ...prev[offerId],
                        selectedVariants: {
                            ...prev[offerId].selectedVariants,
                            [productId]: variantId,
                        }
                    }
                }));

                // Update line items on the server
                await postPurchasesResource.updateLineItems(sessionState.checkoutSessionId!, [
                    {
                        variantId: selectedVariant.variantId,
                        quantity: currentItem.quantity,
                    },
                ]);

                // Refetch order summary after successful line item update
                await fetchOrderSummary(offerId, sessionState.checkoutSessionId!);
            } catch (_error) {
                throw _error;
            } finally {
                // Clear loading state for this specific variant
                setCheckoutSessions(prev => ({
                    ...prev,
                    [offerId]: {
                        ...prev[offerId],
                        loadingVariants: {
                            ...prev[offerId].loadingVariants,
                            [productId]: false,
                        }
                    }
                }));
            }
        },

        getOrderSummary: (offerId: string) => {
            return checkoutSessions[offerId]?.orderSummary ?? null;
        },

        isLoadingVariants: (offerId: string, productId: string) => {
            return checkoutSessions[offerId]?.loadingVariants?.[productId] ?? false;
        },

        isUpdatingOrderSummary: (offerId: string) => {
            return checkoutSessions[offerId]?.isUpdatingSummary || false;
        },

        confirmPurchase: async (offerId: string, _options?: { draft?: boolean; returnUrl?: string }) => {
            const sessionState = checkoutSessions[offerId];
            if (!sessionState?.checkoutSessionId) {
                throw new Error('Checkout session not initialized for this offer');
            }

            if (sessionState.isWhop) {
                // Whop post-purchase: charge using the stored payment method from the original order
                const storeId = session?.storeId;
                if (!storeId) {
                    throw new Error('Store ID not available for Whop payment');
                }
                await postPurchasesResource.chargeWhopPostPurchase({
                    checkoutSessionId: sessionState.checkoutSessionId,
                    orderId,
                    storeId,
                });
            } else {
                // Standard post-purchase: use the generic pay endpoint
                await postPurchasesResource.payWithCheckoutSession(
                    sessionState.checkoutSessionId,
                    orderId
                );
            }
        },
    };
}
